import * as React from 'react';
import styled, { css } from 'styled-components';
import * as PopperJS from '@popperjs/core';
import { PtMuiIconProps } from '@floji-internal/mui-icons';

import IcoError from '@floji-internal/mui-icons/Cancel';
import IcoWarn from '@floji-internal/mui-icons/Warning';
import IcoDropdown from '@floji-internal/mui-icons/ArrowDropDown';
import IcoDropUp from '@floji-internal/mui-icons/ArrowDropUp';

import { Modify } from "../../common/utils";

import { PopperSurface } from '--floponents-web/molecules/PopperSurface';
import { Label } from '../Label/Label';
import { Errors } from '--root/Errors';
import { Picker } from '../../../components/Inputs/Picker';
import { ListEntry, ListEntryText } from '../ListEntry';

// Still a work in progress...

// TODO:    Try to use same pattern as the toggle switch
//          May want to clear error state when onChange fires and await further validation
//          Tidy me up... I'm a quick and dirty modification of the TextInput component for the date and repeat picker

type SelectInputStyleProps = {
    state?: "default" | "warning" | "error" | "disabled"
    size?: "small" | "medium" | "large"
    width?: number | string
    rounded?: boolean
    align?: "center" | "left" | "right";
    resetMargins?: boolean;
    disabled?: boolean
    hasValue?: boolean
    variant?: "normal" | "translucent"
}

const InputContainer = styled.div<SelectInputStyleProps>` 
    display:flex;
    position: relative;
    flex-direction:column;
    text-align: left;
    width: ${props => props.width ?
        typeof props.width == "number" ?
            props.width + 'px' :
            props.width :
        'initial'};
    ${props => props.resetMargins && css`
        margin-left: ${props.theme.spacing(-1)};
        margin-right: ${props.theme.spacing(-1)};
    `}; 
`;
const SelectWrapper = styled.div<SelectInputStyleProps>`
    display: flex;
    align-items: stretch;
    width: 100%;
    min-height: ${props => {
        switch (props.size) {
            case "small": return "32px"
            case "large": return "56px"
            default: return "40px;"
        }
    }};
    box-sizing: border-box;
    padding: ${props => props.theme.spacing(0, props.size == "small" ? 1 : 2)};
    margin:0;
    border: 1px solid ${props =>
        props.state == "error" ?
            props.theme.palette.danger.main :
            props.state == "warning" ?
                props.theme.palette.warning.main :
                props.theme.palette.ui.border
    };
    border-radius: ${props => props.rounded ? props.theme.radius.large : props.theme.radius.small};
    box-shadow: ${props => props.theme.shadows.inputInset};
    ${props => props.variant === "translucent" && css`
        background: ${props.theme.palette.translucentSurface};
        color: ${props.theme.palette.text.primary};
        backdrop-filter: blur(${props.theme.blur});
        border: none;
        box-shadow: none;
    `};

    :focus-within {
        border-color: ${props => props.theme.palette.primary.main};
    }
    align-items: center;
    >span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        ${props => props.size === "small"
        ? props.theme.typography.body2
        : props.theme.typography.body1};
        color: ${props => (props.disabled || !props.hasValue)
        ? props.theme.palette.text.disabled
        : props.state === "error"
            ? props.theme.palette.danger.main
            : props.theme.palette.text.primary
    };
        flex: 1 1 auto;
        min-width: 0; // Needed as browser is giving the input a default width
        border: none;
        outline: none;
        background: transparent;
        margin: 0;
        padding: 0;
        text-align: ${props => props.align || "left"};
        cursor: ${props => props.disabled ? "initial" : "pointer"};
    }
    > svg {
        align-self: center;
        color: ${props =>
        props.state == "disabled" ?
            props.theme.palette.text.disabled :
            props.state == "error" ?
                props.theme.palette.danger.main :
                props.state == "warning" ?
                    props.theme.palette.warning.main :
                    props.theme.palette.text.secondary};
    }
`;
const HelperMessage = styled.div<SelectInputStyleProps>`
    /** Typography */
    ${props => props.theme.typography.body3}
    color: ${props =>
        props.state == "disabled" ?
            props.theme.palette.text.disabled :
            props.state == "error" ?
                props.theme.palette.danger.main :
                props.theme.palette.text.secondary
    };
    margin: 4px 0 0 0;
    padding: ${props => props.theme.spacing(0, 1)};
`;


type tSelectValue<T> = { value?: React.ReactNode, key: T };

type SelectInputProps<T> = Modify<React.HtmlHTMLAttributes<HTMLDivElement>, {
    id?: string,
    optional?: boolean
    disabled?: boolean
    label?: string
    value?: tSelectValue<T>
    placeholder?: React.ReactNode
    message?: string | Errors.FormFieldError // Error and warning props can be ignored if passing an ErrorMessage object
    error?: boolean
    warning?: boolean
    containerRef?: ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement> | null | undefined,
    inputRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined,
    onChange?: (value: tSelectValue<T>) => void
    startIcon?: React.ReactElement<PtMuiIconProps>
    endIcon?: React.ReactElement<PtMuiIconProps>
    size?: "small" | "medium" | "large"
    width?: string | number
    rounded?: boolean
    align?: "center" | "left" | "right"
    resetMargins?: boolean
    placement?: PopperJS.Placement
    options: Array<tSelectValue<T>>
    variant?: "normal" | "translucent"
}>


function SelectInput<T>(props: React.PropsWithChildren<SelectInputProps<T>>): React.ReactElement<any, any> | null {
    const {
        id, label, message, error, warning, optional, containerRef, inputRef,
        startIcon, endIcon, size, width, rounded, disabled, align,
        onChange, value, resetMargins, placeholder, placement = "bottom-start", options
    } = props;

    // Work out what state to render the field in and the message to render
    const errorTest = error || (typeof message == "object" && message.level == "error");
    const warnTest = warning || (typeof message == "object" && message.level == "warning");
    const fieldState = disabled ? "disabled" : errorTest ? "error" : warnTest ? "warning" : undefined;
    const fieldStateIcon = fieldState == "error" ? <IcoError /> : fieldState == "warning" ? <IcoWarn /> : undefined;
    const fieldMessage = typeof message == "object" ? message.message : message;

    const [open, setOpen] = React.useState(false);
    const [active, setActive] = React.useState(0);
    const activeRef = React.useRef(0);
    const targetRef = React.useRef<HTMLDivElement>(null);
    activeRef.current = active;

    const handlePopperClose = (e?: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | React.TouchEvent<Element>) => {
        e?.preventDefault();
        e?.stopPropagation();
        setOpen(false);
    }

    React.useEffect(() => {
        const cleanup = () => window.removeEventListener("keydown", onKeydown);
        const onKeydown = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase();
            switch (key) {
                case "arrowdown":
                    if (activeRef.current >= options.length - 1) setActive(0)
                    else setActive(activeRef.current + 1);
                    break;
                case "arrowup":
                    if (activeRef.current <= 0) setActive(options.length - 1);
                    else setActive(activeRef.current - 1);
                    break;
                case "space":
                case "enter":
                case "tab":
                    onChange?.(options[activeRef.current]!);
                    setOpen(false);
                    break;
            }
        }
        open ?
            window.addEventListener("keydown", onKeydown) :
            cleanup();
        return cleanup;
    }, [open]);

    React.useEffect(() => {
        setActive(options.findIndex(option => option.key == value?.key));
    }, [value]);
    const selectedValue = React.useMemo(() => options.find(v => v.key === value?.key), [value])
    return (
        <InputContainer ref={containerRef} className={props.className} width={width} resetMargins={resetMargins}>
            {label && <Label htmlFor={id} disabled={fieldState === "disabled"} optional={optional}>
                {props.label}
            </Label>}
            <SelectWrapper ref={targetRef} rounded={rounded} state={fieldState} size={size} align={align} disabled={disabled} hasValue={!!value} onClick={() => !disabled && setOpen(!open)} variant={props.variant}>
                {startIcon}
                <span id={id} ref={inputRef}>{selectedValue?.value || selectedValue?.key || placeholder}</span>
                {endIcon || fieldStateIcon}
                {open ? <IcoDropUp /> : <IcoDropdown />}
            </SelectWrapper>
            {fieldMessage && <HelperMessage state={fieldState}>{fieldMessage}</HelperMessage>}

            <PopperSurface
                menu
                triggerRef={targetRef}
                open={open}
                onClose={handlePopperClose}
                placement={placement}
                width={targetRef.current?.offsetWidth}>
                <Picker
                    items={options}
                    keyFor={option => JSON.stringify(option.key)}
                    renderFor={(item) => <ListEntry active={item.key === selectedValue?.key}>
                        <ListEntryText
                            dense
                            primary={item.value || item.key}
                        />
                    </ListEntry>}
                    onPick={(v) => { onChange?.(v); setOpen(false); }} />
            </PopperSurface>
        </InputContainer>
    )
}


export { SelectInput, SelectInputProps, tSelectValue };
